import { createContext, useContext, ReactNode } from 'react';
import { normalizeSeoDescription, stripHtmlToPlainText } from '@webready/shared';
import { useSEOSettings } from './AppSettingsContext';

interface SEOContextType {
  generateMetaTags: (pageTitle?: string, pageDescription?: string, content?: string) => {
    title: string;
    description: string;
    keywords: string;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    canonical: string;
    robots: string;
  };
  generatePageTitle: (pageName: string) => string;
  generateSmartDescription: (content?: string, fallback?: string) => string;
}

const SEOContext = createContext<SEOContextType | undefined>(undefined);

export function SEOProvider({ children }: { children: ReactNode }) {
  const seoSettings = useSEOSettings();

  const generateSmartDescription = (content?: string, fallback?: string): string => {
    if (fallback && fallback.trim()) {
      return normalizeSeoDescription(fallback);
    }

    if (content) {
      const normalized = normalizeSeoDescription(stripHtmlToPlainText(content));
      if (normalized) {
        return normalized;
      }
    }

    // Fallback to default description
    return seoSettings.metaDescription;
  };

  const generateMetaTags = (pageTitle?: string, pageDescription?: string, content?: string) => {
    const title = pageTitle 
      ? `${pageTitle} | ${seoSettings.appName}`
      : seoSettings.metaTitle;
    
    const description = generateSmartDescription(content, pageDescription);

    return {
      title,
      description,
      keywords: seoSettings.metaKeywords,
      ogTitle: pageTitle ? `${pageTitle} | ${seoSettings.appName}` : seoSettings.ogTitle,
      ogDescription: description,
      ogImage: seoSettings.ogImageUrl || '',
      canonical: seoSettings.canonicalUrl || '',
      robots: seoSettings.robotsDirectives,
    };
  };

  const generatePageTitle = (pageName: string) => {
    return `${pageName} | ${seoSettings.appName}`;
  };

  return (
    <SEOContext.Provider value={{ generateMetaTags, generatePageTitle, generateSmartDescription }}>
      {children}
    </SEOContext.Provider>
  );
}

export const useSEO = () => {
  const context = useContext(SEOContext);
  if (!context) {
    throw new Error('useSEO must be used within SEOProvider');
  }
  return context;
};
